import { useCallback } from 'react';
import toast from 'react-hot-toast';
import { Toast, isAlertTone } from 'voyager';
import type { ToastTone } from 'voyager';

import { useTranslations } from '@lang';

import { ToastDetail, ToastSlide } from './Notifications.styles';

export type ShowToastOptions = {
  tone?: ToastTone;
  detail?: string;
  label?: string;
  duration?: number;
  id?: string;
  dismissible?: boolean;
  dismissLabel?: string;
};

const TOAST_POSITION = 'top-right' as const;

const DEFAULT_DURATION_MS = 6000;

const ERROR_TONES: ToastTone[] = ['danger'];

const ERROR_DURATION_MULTIPLIER = 2;

export const useToast = () => {
  const { t } = useTranslations();

  const show = useCallback(
    (message: string, options: ShowToastOptions = {}) => {
      const { tone = 'neutral', detail, label, id } = options;

      const isError = ERROR_TONES.includes(tone);

      const duration =
        options.duration ?? DEFAULT_DURATION_MS * (isError ? ERROR_DURATION_MULTIPLIER : 1);

      const dismissible = options.dismissible ?? isAlertTone(tone);

      const dismissLabel = dismissible
        ? options.dismissLabel ?? t('bookingRequestModal.dismissAlert')
        : undefined;

      return toast.custom(
        (activeToast) => (
          <ToastSlide $isVisible={activeToast.visible} $isMeasured={Boolean(activeToast.height)}>
            <Toast
              tone={tone}
              label={label}
              dismissLabel={dismissLabel}
              onDismiss={dismissible ? () => toast.dismiss(activeToast.id) : undefined}
            >
              {message}
              {detail && <ToastDetail>{detail}</ToastDetail>}
            </Toast>
          </ToastSlide>
        ),
        { id, duration, position: TOAST_POSITION }
      );
    },
    [t]
  );

  const dismiss = useCallback((id: string) => toast.dismiss(id), []);

  return { show, dismiss };
};
